import { AnyAction, Dispatch, ListenerEffect, ThunkDispatch } from '@reduxjs/toolkit';
import { uuid } from 'common/utils';
import { addBroadcastInfoMessage, removeAppMessage, selectAlertById } from 'modules/alerts/state';
import { selectActiveEventDetails } from 'store/apis/event';
import { selectUserAccountId } from 'user/api';

import { demoteUserToAudience } from '../eventSlice.actions';
import {
  selectBroadcast,
  selectEventHosts,
  selectIsCurrentUserEventHost,
  selectIsParticipantSpeaker,
  selectParticipantsWithRaisedHands,
  selectRaisedHandAlertText,
} from '../selectors';
import { isHost } from '../utils';

import type { RootState } from 'store';

type CombinedDispatch = Dispatch & ThunkDispatch<RootState, unknown, AnyAction>;

type Effect = ListenerEffect<AnyAction, RootState, CombinedDispatch>;

const HAND_RAISED_NOTIFICATION_ID = uuid();

const selectDisplayedHandRaisedAlertText = (state: RootState): string | undefined => {
  const alert = selectAlertById(HAND_RAISED_NOTIFICATION_ID)(state);
  if (!alert) return undefined;
  return typeof alert.message === 'string' ? alert.message : alert.message.message;
};

/**
 * Shows the toast, or refreshes its text. Re-adding an alert remounts it and logs an `alert_displayed`
 * RUM action, so this is a no-op while the text is unchanged: the actions that drive it fire on every
 * Ably presence update (each audience join, each role change), not only when a hand actually moves.
 */
const showHandRaisedAlert = (dispatch: CombinedDispatch, state: RootState) => {
  const message = selectRaisedHandAlertText(state);
  if (selectDisplayedHandRaisedAlertText(state) === message) return;

  dispatch(
    addBroadcastInfoMessage({
      message,
      id: HAND_RAISED_NOTIFICATION_ID,
      timeout: 0,
      icon: 'RaisedHandIcon',
    })
  );
};

export const displayHandRaisedAlert: Effect = async (_, { dispatch, getState }) => {
  const state = getState();
  const broadcast = selectBroadcast(state);
  const eventDetails = selectActiveEventDetails(state);
  const accountId = selectUserAccountId(state);

  if (!(broadcast && eventDetails && accountId)) return;

  const participantsWithRaisedHands = selectParticipantsWithRaisedHands(state);
  const isCurrentUserSpeaker = selectIsParticipantSpeaker(state, accountId);
  const isUserHost = selectIsCurrentUserEventHost(state);
  const shouldSeeHandRaisedAlert = isUserHost || isCurrentUserSpeaker;

  if (!shouldSeeHandRaisedAlert) return;

  const otherHandsRaised = participantsWithRaisedHands.filter((id) => id !== accountId);

  if (otherHandsRaised.length === 0) return;

  showHandRaisedAlert(dispatch, state);
};

export const updateHandRaisedAlert: Effect = async (_, { dispatch, getState }) => {
  const state = getState();

  const isHandRaisedNotificationDisplayed = selectAlertById(HAND_RAISED_NOTIFICATION_ID)(state);
  if (!isHandRaisedNotificationDisplayed) return;

  const accountId = selectUserAccountId(state);
  if (!accountId) return;

  const participantsWithRaisedHands = selectParticipantsWithRaisedHands(state);
  const otherHandsRaised = participantsWithRaisedHands.filter((id) => id !== accountId);

  if (otherHandsRaised.length === 0) {
    dispatch(removeAppMessage(HAND_RAISED_NOTIFICATION_ID));
  } else {
    showHandRaisedAlert(dispatch, state);
  }
};

export const userDemotedEffect: Effect = async (_, { dispatch, getState }) => {
  const state = getState();
  const isUserHost = selectIsCurrentUserEventHost(state);

  if (!isUserHost) {
    const eventHosts = selectEventHosts(state);
    const newAudience = state.event.audience.filter(({ id }) => isHost(eventHosts, id));
    dispatch(demoteUserToAudience({ newAudience }));
  }
};
